import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { vehicleOwnerService } from '../../services/vehicleOwnerService';
import vehicleService from '../../services/vehicleService';
import { useAuth } from '../../context/AuthContext';
import Alert from '../common/Alert';
import { Users, Truck, X, Search } from 'lucide-react';
import { PageLayout, Button } from '../common/index.js';

export default function AddVehicleOwner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('owner');
  const [allVehicles, setAllVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(true);
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    owner_name: '',
    owner_email: '',
    owner_contact: '',
    bank_account: '',
    ifsc_code: '',
    vehicle_owner_percentage: '',
    vehicles: [],
  });

  const canManage = user?.role === 'System Admin' || user?.role === 'Admin' || user?.role === 'Accountant';

  useEffect(() => {
    if (!canManage) {
      Alert.error('You do not have permission to add vehicle owners');
      navigate('/vehicle-owners');
      return;
    }
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      setVehiclesLoading(true);
      const response = await vehicleService.getAllVehicles();
      const data = response.data?.data || response.data || [];
      setAllVehicles(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setVehiclesLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.owner_name.trim()) newErrors.owner_name = 'Owner name is required';
    if (!formData.owner_email.trim()) {
      newErrors.owner_email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.owner_email)) {
      newErrors.owner_email = 'Enter a valid email address';
    }
    if (!formData.owner_contact.trim()) {
      newErrors.owner_contact = 'Contact number is required';
    } else if (!/^\d{10,15}$/.test(formData.owner_contact.replace(/\s/g, ''))) {
      newErrors.owner_contact = 'Enter a valid contact number (10–15 digits)';
    }
    if (formData.vehicle_owner_percentage !== '' && formData.vehicle_owner_percentage !== null) {
      const pct = Number(formData.vehicle_owner_percentage);
      if (isNaN(pct) || pct < 0 || pct > 100) {
        newErrors.vehicle_owner_percentage = 'Percentage must be between 0 and 100';
      }
    }
    if (formData.ifsc_code && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc_code.toUpperCase())) {
      newErrors.ifsc_code = 'Enter a valid IFSC code (e.g. SBIN0001234)';
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const toggleVehicle = (vehicleId) => {
    const id = String(vehicleId);
    setFormData(prev => ({
      ...prev,
      vehicles: prev.vehicles.map(String).includes(id)
        ? prev.vehicles.filter(v => String(v) !== id)
        : [...prev.vehicles, id],
    }));
  };

  const handleSelectAll = (checked) => {
    const allFilteredIds = filteredVehicles.map(v => String(v.vehicle_id));
    setFormData(prev => ({
      ...prev,
      vehicles: checked
        ? [...new Set([...prev.vehicles.map(String), ...allFilteredIds])]
        : prev.vehicles.filter(id => !allFilteredIds.includes(String(id))),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Switch to owner tab if there are owner info errors
      if (validationErrors.owner_name || validationErrors.owner_email || validationErrors.owner_contact || validationErrors.vehicle_owner_percentage) {
        setActiveTab('owner');
      } else if (validationErrors.ifsc_code) {
        setActiveTab('bank');
      }
      return;
    }

    try {
      setLoading(true);
      const payload = {
        owner_name: formData.owner_name.trim(),
        owner_email: formData.owner_email.trim(),
        owner_contact: formData.owner_contact.trim(),
        bank_account: formData.bank_account.trim() || null,
        ifsc_code: formData.ifsc_code.toUpperCase().trim() || null,
        vehicle_owner_percentage: formData.vehicle_owner_percentage !== '' ? Number(formData.vehicle_owner_percentage) : 0,
        vehicles: formData.vehicles,
      };
      await vehicleOwnerService.createOwner(payload);
      Alert.success('Vehicle owner created successfully');
      setTimeout(() => navigate('/vehicle-owners'), 1000);
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to create vehicle owner');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    const hasData = formData.owner_name || formData.owner_email || formData.owner_contact;
    if (hasData) {
      const confirmed = await Alert.confirm(
        'You have unsaved changes. Are you sure you want to cancel?',
        'Unsaved Changes'
      );
      if (!confirmed) return;
    }
    navigate('/vehicle-owners');
  };

  const filteredVehicles = allVehicles.filter(v =>
    !vehicleSearch.trim() ||
    v.vehicle_number?.toLowerCase().includes(vehicleSearch.toLowerCase()) ||
    v.vehicle_name?.toLowerCase().includes(vehicleSearch.toLowerCase()) ||
    v.vehicle_type?.toLowerCase().includes(vehicleSearch.toLowerCase())
  );

  const selectedVehicleObjects = allVehicles.filter(v => formData.vehicles.map(String).includes(String(v.vehicle_id)));

  const tabClass = (tab) =>
    `pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
      activeTab === tab
        ? 'border-brand text-secondary'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`;

  return (
    <PageLayout title="Add New Vehicle Owner" icon={Users} maxWidth="max-w-7xl">
      <form onSubmit={handleSubmit} className="bg-white rounded-card shadow-card p-4 sm:p-8 space-y-6 sm:space-y-8">

        <div className="border-b border-gray-200">
          <nav className="flex space-x-8">
            <button type="button" onClick={() => setActiveTab('owner')} className={tabClass('owner')}>
              Owner Information
            </button>
            <button type="button" onClick={() => setActiveTab('bank')} className={tabClass('bank')}>
              Bank Details
            </button>
            <button type="button" onClick={() => setActiveTab('vehicles')} className={tabClass('vehicles')}>
              Assign Vehicles
              {formData.vehicles.length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                  {formData.vehicles.length}
                </span>
              )}
            </button>
          </nav>
        </div>

                  {/* Owner Information Tab */}
                  {activeTab === 'owner' && (
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Owner Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">

                        {/* Owner Name */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Owner Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="owner_name"
                            value={formData.owner_name}
                            onChange={handleChange}
                            placeholder="Enter owner full name"
                            className={`w-full px-3 sm:px-4 py-2 border rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base ${errors.owner_name ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                          />
                          {errors.owner_name && <p className="text-xs text-red-500 mt-1">{errors.owner_name}</p>}
                        </div>

                        {/* Email */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Email Address <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            name="owner_email"
                            value={formData.owner_email}
                            onChange={handleChange}
                            placeholder="email@example.com"
                            className={`w-full px-3 sm:px-4 py-2 border rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base ${errors.owner_email ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                          />
                          {errors.owner_email && <p className="text-xs text-red-500 mt-1">{errors.owner_email}</p>}
                        </div>

                        {/* Contact */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Contact Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            name="owner_contact"
                            value={formData.owner_contact}
                            onChange={handleChange}
                            placeholder="10-digit mobile number"
                            className={`w-full px-3 sm:px-4 py-2 border rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base ${errors.owner_contact ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                          />
                          {errors.owner_contact && <p className="text-xs text-red-500 mt-1">{errors.owner_contact}</p>}
                        </div>

                        {/* Commission % */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Commission Percentage (%)
                          </label>
                          <input
                            type="number"
                            name="vehicle_owner_percentage"
                            value={formData.vehicle_owner_percentage}
                            onChange={handleChange}
                            placeholder="0"
                            min="0"
                            max="100"
                            className={`w-full px-3 sm:px-4 py-2 border rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base ${errors.vehicle_owner_percentage ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                          />
                          {errors.vehicle_owner_percentage && <p className="text-xs text-red-500 mt-1">{errors.vehicle_owner_percentage}</p>}
                        </div>

                      </div>
                    </div>
                  )}

                  {/* Bank Details Tab */}
                  {activeTab === 'bank' && (
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Bank Details <span className="text-gray-400 font-normal normal-case text-sm ml-1">(optional)</span>
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">

                        {/* Bank Account */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Bank Account Number
                          </label>
                          <input
                            type="text"
                            name="bank_account"
                            value={formData.bank_account}
                            onChange={handleChange}
                            placeholder="Enter account number"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base font-mono"
                          />
                        </div>

                        {/* IFSC Code */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            IFSC Code
                          </label>
                          <input
                            type="text"
                            name="ifsc_code"
                            value={formData.ifsc_code}
                            onChange={(e) => handleChange({ target: { name: 'ifsc_code', value: e.target.value.toUpperCase() } })}
                            placeholder="e.g. SBIN0001234"
                            maxLength={11}
                            className={`w-full px-3 sm:px-4 py-2 border rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base font-mono uppercase ${errors.ifsc_code ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                          />
                          {errors.ifsc_code && <p className="text-xs text-red-500 mt-1">{errors.ifsc_code}</p>}
                        </div>

                      </div>
                    </div>
                  )}

                  {/* Assign Vehicles Tab */}
                  {activeTab === 'vehicles' && (
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Assign Vehicles <span className="text-gray-400 font-normal normal-case text-sm ml-1">(optional)</span>
                      </h3>

                      {/* Selected chips */}
                      {selectedVehicleObjects.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {selectedVehicleObjects.map(v => (
                            <span
                              key={v.vehicle_id}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-sm text-xs font-medium"
                            >
                              <Truck className="w-3 h-3" />
                              {v.vehicle_number || v.vehicle_id}
                              <button
                                type="button"
                                onClick={() => toggleVehicle(v.vehicle_id)}
                                className="ml-0.5 hover:text-red-600 transition-colors"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Vehicle search */}
                      <div className="relative mb-2">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                        <input
                          type="text"
                          value={vehicleSearch}
                          onChange={e => setVehicleSearch(e.target.value)}
                          placeholder="Search vehicles by number or type..."
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                        />
                      </div>

                      {/* Vehicle list */}
                      <div className="border border-gray-200 rounded-sm overflow-hidden max-h-64 overflow-y-auto">
                        {vehiclesLoading ? (
                          <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand" />
                          </div>
                        ) : filteredVehicles.length === 0 ? (
                          <div className="flex flex-col items-center py-8 text-gray-400">
                            <Truck className="w-7 h-7 mb-2 text-gray-300" />
                            <p className="text-sm">
                              {allVehicles.length === 0 ? 'No vehicles found in the system' : 'No vehicles match your search'}
                            </p>
                          </div>
                        ) : (
                          <>
                            <label className="flex items-center gap-3 px-3 py-2.5 cursor-pointer border-b border-gray-200 bg-gray-50 hover:bg-gray-100 select-none sticky top-0 z-10">
                              <input
                                type="checkbox"
                                checked={filteredVehicles.length > 0 && filteredVehicles.every(v => formData.vehicles.map(String).includes(String(v.vehicle_id)))}
                                onChange={e => handleSelectAll(e.target.checked)}
                                className="accent-blue-600 w-4 h-4 flex-shrink-0"
                              />
                              <span className="text-sm font-semibold text-secondary">
                                Select All ({filteredVehicles.length})
                              </span>
                            </label>
                            {filteredVehicles.map((vehicle, idx) => {
                            const selected = formData.vehicles.map(String).includes(String(vehicle.vehicle_id));
                            return (
                              <label
                                key={vehicle.vehicle_id}
                                className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors select-none ${
                                  idx !== filteredVehicles.length - 1 ? 'border-b border-gray-100' : ''
                                } ${selected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={() => toggleVehicle(vehicle.vehicle_id)}
                                  className="accent-blue-600 w-4 h-4 flex-shrink-0"
                                />
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <div className="p-1 bg-gray-100 rounded-sm flex-shrink-0">
                                    <Truck className="w-3.5 h-3.5 text-secondary" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-sm font-semibold text-secondary truncate">
                                      {vehicle.vehicle_number || vehicle.vehicle_id}
                                    </p>
                                    {vehicle.vehicle_name && (
                                      <p className="text-xs text-gray-400 truncate">{vehicle.vehicle_name}</p>
                                    )}
                                    {vehicle.vehicle_type && (
                                      <p className="text-xs text-gray-400 capitalize">
                                        {vehicle.vehicle_type.replace(/_/g, ' ')}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                {vehicle.status && (
                                  <span className={`text-xs font-medium px-1.5 py-0.5 rounded-sm flex-shrink-0 ${
                                    vehicle.status === 'active'
                                      ? 'bg-green-50 text-green-700'
                                      : 'bg-gray-100 text-gray-500'
                                  }`}>
                                    {vehicle.status.charAt(0).toUpperCase() + vehicle.status.slice(1)}
                                  </span>
                                )}
                              </label>
                            );
                          })}
                          </>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        Select one or more vehicles to assign to this owner. Conflicts are validated on save.
                      </p>
                    </div>
                  )}

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-6 border-t border-gray-100">
          <Button type="button" variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="submit" loading={loading} className="sm:ml-auto">
            {loading ? 'Creating Owner...' : 'Create Vehicle Owner'}
          </Button>
        </div>
      </form>
    </PageLayout>
  );
}